import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useStoreState } from "easy-peasy";

import { reportCurrencyInfo, reportSelectedPortfolios } from "store/activePortfolios";

/**
 * Currency facts of the portfolios a report's own SALKKU filter has selected (#1409):
 * { currency, mixedCurrency, currencies, currencyOptions }. `selected` is the filter's
 * options ({ id }) or ids.
 */
export const useReportCurrencyInfo = (selected) => {
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);
  return useMemo(
    () => reportCurrencyInfo(reportSelectedPortfolios(selected, portfolios)),
    [selected, portfolios]
  );
};

/**
 * Info line under a report's filters when the selected portfolios are in different currencies:
 * "Salkut ovat eri valuutoissa (EUR, USD). Summat on muunnettu valuuttaan EUR."
 *
 * The status element is always rendered so a screen reader hears the line when it appears or
 * the currency changes; it is empty for one currency. Nothing is said for "nothing" (no
 * conversion), because then the amounts are not converted.
 */
const ReportCurrencyNote = ({ selected, currency, className = "" }) => {
  const { t } = useTranslation();
  const { mixedCurrency, currencies } = useReportCurrencyInfo(selected);
  const show = mixedCurrency && currency && currency !== "nothing";

  return (
    <div role="status" className={show ? `report-currency-note d-flex align-items-start small ${className}` : undefined}>
      {show && (
        <>
          <i className="fas fa-info-circle mr-2 mt-1" aria-hidden="true" />
          <span>
            {t(
              "report_mixed_currency_note",
              "Salkut ovat eri valuutoissa ({{currencies}}). Summat on muunnettu valuuttaan {{currency}}.",
              { currencies: currencies.join(", "), currency }
            )}
          </span>
        </>
      )}
    </div>
  );
};

export default ReportCurrencyNote;
